import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { Globe, MessageSquare, Building2, Smartphone, Database, Gauge } from 'lucide-react';

const ServicesSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const services = [
    {
      icon: Globe,
      title: 'Desenvolvimento Web',
      description: 'Sites e aplicações web modernas, responsivas e otimizadas para conversão.',
      features: ['React & Next.js', 'UI/UX Design', 'SEO Otimizado', 'Performance'],
      color: 'from-blue-500 to-indigo-600',
    },
    {
      icon: Building2,
      title: 'Soluções Empresariais',
      description: 'Sistemas personalizados para gestão, automação e otimização de processos.',
      features: ['ERP & CRM', 'Dashboards', 'Integrações', 'Automação'],
      color: 'from-purple-500 to-pink-600',
    },
    {
      icon: MessageSquare,
      title: 'Consultoria Técnica',
      description: 'Orientação especializada para decisões tecnológicas e arquitetura de software.',
      features: ['Arquitetura', 'Code Review', 'Mentoria', 'Estratégia Tech'],
      color: 'from-indigo-500 to-purple-600',
    },
    {
      icon: Smartphone,
      title: 'Aplicações Mobile',
      description: 'Apps nativos e híbridos para iOS e Android com experiência premium.',
      features: ['React Native', 'UI Nativa', 'Offline-First', 'Push Notifications'],
      color: 'from-cyan-500 to-blue-600',
    },
    {
      icon: Database,
      title: 'APIs & Backend',
      description: 'Desenvolvimento de APIs robustas, escaláveis e seguras.',
      features: ['REST & GraphQL', 'Microserviços', 'Cloud Deploy', 'Segurança'],
      color: 'from-emerald-500 to-teal-600',
    },
    {
      icon: Gauge,
      title: 'Otimização & Suporte',
      description: 'Manutenção, melhorias de performance e suporte contínuo.',
      features: ['Performance', 'Monitoramento', 'Updates', 'Suporte 24/7'],
      color: 'from-orange-500 to-red-600',
    },
  ];

  return (
    <section id="servicos" className="py-24 bg-gray-50" ref={ref} aria-label="Nossos Serviços">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-sm font-semibold text-primary-600 uppercase tracking-wide mb-3">
            Nossos Serviços
          </h2>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Soluções <strong className="text-gradient">completas</strong> para seu negócio
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Da concepção ao lançamento, oferecemos todos os serviços necessários para transformar sua visão em realidade.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group bg-white rounded-xl p-8 shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className={`bg-gradient-to-br ${service.color} w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <service.icon className="text-white" size={32} />
              </div>

              <h4 className="text-2xl font-bold text-gray-900 mb-3">
                {service.title}
              </h4>
              <p className="text-gray-600 mb-6">
                {service.description}
              </p>

              <ul className="space-y-2">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center text-sm text-gray-700">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-500 mr-2" />
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="text-center mt-16"
        >
          <button
            onClick={() => document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-10 py-4 rounded-lg font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
          >
            Solicitar Orçamento
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
